import { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useEmergencyTotp from '../../hooks/useEmergencyTotp';
import { toast } from 'react-toastify';
import funzoneLogo from '../../assets/logos/funzone.webp';
import FunzoneStyledQr from '../components/FunzoneStyledQr';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

export default function EmergencyTotpSetup() {
  const theme = useTheme();
  const { generate } = useEmergencyTotp();
  const [issuer, setIssuer] = useState('FunZone');
  const [accountName, setAccountName] = useState('Locationserver');
  const [result, setResult] = useState(null);
  const [qrReady, setQrReady] = useState(false);

  const qrData = useMemo(
    () => result?.otpauthUri || result?.otpauthURL || result?.otpauth || '',
    [result]
  );

  const handleGenerate = async () => {
    try {
      setQrReady(false);
      const data = await generate({ issuer, accountName });
      setResult(data);
      toast.success('TOTP-Secret erzeugt');
    } catch (_) {}
  };

  // mark ready when we have data (FunzoneStyledQr shows its own spinner)
  // keep state for backward-compatible layout tweaks
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const _qrData = qrData;

  return (
    <Box sx={{ p: 2, ...viewRootTextPrimarySx }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant='h6'>Notfallmodus TOTP-Setup</Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Erzeuge ein TOTP-Secret für den Locationserver. Nutze dafür z. B. die Microsoft
          Authenticator App.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            label='Issuer'
            value={issuer}
            onChange={e => setIssuer(e.target.value)}
            size='small'
          />
          <TextField
            label='Account Name'
            value={accountName}
            onChange={e => setAccountName(e.target.value)}
            size='small'
          />
          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
            <Button variant='contained' onClick={handleGenerate} fullWidth={false}>
              Secret erzeugen
            </Button>
          </Box>
        </Box>

        {result && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}
            >
              <Box sx={{ width: '100%', maxWidth: 360 }}>
                <FunzoneStyledQr
                  data={qrData}
                  size={360}
                  maxSize={360}
                  centerLogoSrc={funzoneLogo}
                />
              </Box>
              <Box sx={{ display: 'grid', gap: 2 }}>
                <div>
                  <Typography variant='subtitle1'>Secret</Typography>
                  <Box
                    sx={{
                      fontFamily: 'monospace',
                      p: 1,
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {result.secretBase32}
                  </Box>
                </div>
                <div>
                  <Typography variant='subtitle1'>QR/otpauth</Typography>
                  <Box
                    sx={{
                      fontFamily: 'monospace',
                      p: 1,
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {qrData}
                  </Box>
                </div>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
